import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Button from "../Button/Button";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import "./SignaturePad.scss";

export interface SignaturePadHandle {
  clear: () => void;
  isSigned: () => boolean;
  getBlob: () => Promise<Blob>;
  drawFromUrl: (url: string) => void;
}

interface SignaturePadProps {
  label?: string;
  statusId?: string;
  onSignedChange?: (signed: boolean) => void;
  error?: boolean;
}

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ label, statusId, onSignedChange, error }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawingRef = useRef<boolean>(false);
    const [signed, setSigned] = useState<boolean>(false);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return undefined;

      const syncSize = () => {
        const bounds = canvas.getBoundingClientRect();
        canvas.width = bounds.width;
        canvas.height = 90;
      };

      syncSize();
      window.addEventListener("resize", syncSize);
      return () => window.removeEventListener("resize", syncSize);
    }, []);

    useEffect(() => {
      onSignedChange?.(signed);
    }, [onSignedChange, signed]);

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          const canvas = canvasRef.current!;
          canvas.getContext("2d")!.clearRect(0, 0, canvas.width, canvas.height);
          setSigned(false);
        },
        isSigned: () => signed,
        getBlob: () =>
          new Promise<Blob>((resolve, reject) => {
            const canvas = canvasRef.current;
            if (!canvas) return reject(new Error("No canvas"));
            canvas.toBlob(
              (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
              "image/png",
            );
          }),
        drawFromUrl: (url: string) => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const tryDraw = () => {
              if (canvas.width === 0 || canvas.height === 0) {
                requestAnimationFrame(tryDraw);
                return;
              }
              const ctx = canvas.getContext("2d")!;
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              setSigned(true);
              onSignedChange?.(true);
            };
            tryDraw();
          };
          img.src = url;
        },
      }),
      [signed, onSignedChange],
    );

    const getPoint = (
      event:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      const source = "touches" in event ? event.touches[0] : event;
      return {
        x: (source.clientX - rect.left) * (canvas.width / rect.width),
        y: (source.clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const beginDraw = (
      event:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      event.preventDefault();
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d")!;
      const point = getPoint(event);
      drawingRef.current = true;
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    };

    const draw = (
      event:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      event.preventDefault();
      if (!drawingRef.current) return;
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d")!;
      const point = getPoint(event);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#111";
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      if (!signed) setSigned(true);
    };

    const stopDraw = () => {
      drawingRef.current = false;
    };

    return (
      <div className="signatureBlock">
        {label && <p className="signatureLabel">{label}</p>}
        <div
          className={`signatureCanvas${signed ? " signatureCanvas--signed" : ""}${error ? " error-input-red" : ""}`}
        >
          <canvas
            ref={canvasRef}
            className="signaturePad"
            onMouseDown={beginDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={beginDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
            aria-label={label}
          />
          {!signed && <div className="signatureHint">Sign here ✍</div>}
        </div>
        <ErrorMessage
          touched={error}
          error="Signature is required before submitting"
        />
        {(statusId || label) && (
          <div className="signatureActions">
            <Button
              className="btn btnSecondary btnSmall"
              onClick={() => {
                const canvas = canvasRef.current!;
                canvas
                  .getContext("2d")!
                  .clearRect(0, 0, canvas.width, canvas.height);
                setSigned(false);
              }}
            >
              Clear
            </Button>
            {statusId && (
              <span
                id={statusId}
                className={`signatureStatus ${signed ? "signed" : ""}`.trim()}
              >
                {signed ? "✓ Signed" : "Not yet signed"}
              </span>
            )}
          </div>
        )}
      </div>
    );
  },
);

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
